import { sortBy, uniqBy } from "lodash";
import { action, computed, observable } from "mobx";
import {
  isPickupFiltered,
  isStopFiltered,
  isFuturePickup,
  isTodaysPickup
} from "../components/modules/Dispatch/RouteExecution/filterUtils";
import { format, isSameDay } from "date-fns";
import appConstants from "../appConstants";
import { getUTCDate } from "../components/dataGrid/gridUtilities";
import {
  isDriverReadyForDispatch,
  isWithinTime
} from "../components/modules/Dispatch/RouteExecution/utils";
import { utcToZonedTime } from "date-fns-tz";
import {
  getDuplicatePickupsByPickupId,
  isPossibleMergeFilterApplied,
} from "../components/modules/Dispatch/utils/utils";

class DispatchStore {
  //original data Sources
  @observable pickupsList = [];
  @observable currentGridName = "";
  @observable kpiData = [];
  @observable selectedRouteNameList = [];
  @observable selectedPickupIdList = [];
  @observable filteredStopsList = [];
  @observable stopsFilter = [];
  @observable stopsFilterData = [];
  @observable tractors = [];
  @observable pickupsFilter = "";
  @observable driverUnReadMessageCountList = [];
  @observable selectedDriverRouteId = null;
  @observable selectedDispatchDriverIdList = [];
  @observable availableDriverList = [];
  @observable dispatchedDrivers = [];
  @observable terminals = [];
  @observable pickupRestrictions = null;
  @observable driversRouteGridMessage = "loading";
  @observable pickupsGridMessage = "loading";
  @observable driversGridMessage = "loading";
  @observable dispatchGridMessage = "loading";
  @observable staticClientData = null;
  @observable hazmatInfo = null;
  @observable showAllDrivers = true;
  @observable terminalOffset = null;
  @observable dispatchedDriverNotifications = [];
  @observable movePickupTerminalList = [];
  @observable duplicatePickups = [];
  @observable driverLocations = {}

  @action addDispatchedDriverNotification(messages) {
    this.dispatchedDriverNotifications.replace([
      ...messages,
      ...this.dispatchedDriverNotifications,
    ]);
  }

  @action updateNotificationReadStatus(driverId) {
    const updatedMessages = this.dispatchedDriverNotifications.filter(msg=>
      msg.sender.toUpperCase() !== driverId.toUpperCase()
    );
    this.dispatchedDriverNotifications.replace(updatedMessages);
  }

  @action addPickups(pickupsList) {
    this.pickupsList.replace(pickupsList);
  }
  @action setGridName(name) {
    this.currentGridName = name;
  }
  @action setTerminals(terminals) {
    this.terminals.replace(terminals);
  }

  @action setPickupRestrictions(pickupRestrictions) {
    this.pickupRestrictions = pickupRestrictions;
  }

  @action setTractors(tractors) {
    this.tractors.replace(tractors);
  }

  @action setAvailableDriverList(driverList) {
    this.availableDriverList.replace(driverList);
  }

  @action setTerminalOffset(terminalOffset) {
    this.terminalOffset = terminalOffset;
  }

  @action updateKPIData(kpiData) {
    this.kpiData.replace(kpiData);
  }

  @action updatePickupsFilter(option) {
    this.pickupsFilter = option;
  }

  @action updateMapFilter(stopFilter) {
    this.stopsFilter.replace(stopFilter);
  }

  @action addDriverLocation(driverId, latitude, longitude, eventTime) {
    const lastConnectedTime = eventTime?.length > 5 ? format(
      utcToZonedTime(eventTime, this.terminalOffset),
      appConstants.DATETYPES.TIME.format
    ) : eventTime;
    this.driverLocations = {
      ...this.driverLocations,
      [driverId.toUpperCase()]: {
        latitude,
        longitude,
        lastConnectedTime,
      },
    };
  }

  @action addDispatchedDrivers(dispatchedDrivers, routeColorList) {
    let colorIndex = -1;
    this.driverLocations = {};
    this.dispatchedDrivers.replace(
      dispatchedDrivers.map(
        ({
          lastKnownLatitude,
          lastKnowLongitude,
          lastConnectedTime,
          ...driver
        }) => {
          colorIndex++;
          this.addDriverLocation(driver.driverId, lastKnownLatitude, lastKnowLongitude, lastConnectedTime);
          return {
            ...driver,
            unReadCount: this.getUnreadMessageCounts(driver.driverId),
            dispatchRoutes:
              driver.dispatchRoutes?.map((route) => ({
                ...route,
                color: routeColorList[colorIndex % routeColorList.length],
              })) ?? [],
          };
        }
      )
    );

    if (this.selectedPickupsList.length > 0) {
      const refreshedSelectedPickup = this.pickupsList
        .filter((pickup) =>
          this.selectedPickupIdList.includes(pickup.pickupId.toLowerCase())
        )
        .map((pickup) => pickup.pickupId);
      this.updateSelectedPickups(refreshedSelectedPickup);
    } else {
      this.updateSelectedPickups([]);
    }
  }

  @action updateSelectedPickups(selectedPickupIds) {
    this.selectedPickupIdList.replace(
      selectedPickupIds.map((pickupId) => pickupId.toLowerCase())
    );
  }

  @action updateSelectedRoutes(routeIdList) {
    this.selectedRouteNameList.replace(routeIdList);
  }

  @action updateSelectedDispatchDriver(driverIdList) {
    this.selectedDispatchDriverIdList.replace(driverIdList);
  }

  @computed get filteredPickups() {
    const isPossibleMergeFilter = isPossibleMergeFilterApplied(
      this.pickupsFilter.value
    );
    return this.pickupsList.filter((pickup) =>
      isPickupFiltered({
        pickup,
        ...(isPossibleMergeFilter && {
          duplicatePickups: getDuplicatePickupsByPickupId(
            this.duplicatePickups,
            pickup.pickupId
          ),
        }),
        filters: [this.pickupsFilter],
        terminalOffset: this.terminalOffset,
      })
    );
  }

  @action setStaticClientData(clientData) {
    this.staticClientData = clientData;
  }

  @action getStaticClientData() {
    return this.staticClientData;
  }

  @computed get returnCodeForReturnTerminal() {
    return this.returnCodes.filter(
      (returnCode) => returnCode.displayReturnToTerminal
    );
  }

  @computed get pickupAccessorialList() {
    return this.staticClientData.clientCodes.accessorialList.filter(
      (data) => data.type === appConstants.PICKUP_ACCESSORIAL_CODE
    );
  }

  @action setHazmatInfo(hazmatInfo) {
    this.hazmatInfo = hazmatInfo;
  }

  @computed get hazmatInfoList() {
    return this.hazmatInfo;
  }

  @computed get deliveryAccessorialList() {
    return this.staticClientData.clientCodes.accessorialList.filter(
      (data) => data.type === appConstants.DELIVERY_ACCESSORIAL_CODE
    );
  }

  @computed get tractorList() {
    return this.tractors.map((tractor) => ({
      label: tractor.tractorNumber.toString(),
      labelSuffix: tractor.isCityTrailer ? "(City)" : "(Linehaul)",
      value: tractor.tractorNumber,
    }));
  }

  @computed get dispatchRouteList() {
    return this.routesList
      .filter((route) => route.routeId)
      .map((route) => ({
        label: route.routeName,
        value: route.routeId,
      }));
  }

  @computed get dispatchedDriversList() {
    return this.dispatchedDrivers.map(driver => (
      {
        label: `#${driver.driverNumber}`,
        value: driver.driverId,
        labelSuffix: `(${driver.driverName})`,
    }))
  }

  @computed get activeDriversList() {
    const activeDriversList = this.routesList.map((driver) => ({
      label: driver.driverName,
      value: driver.driverId,
    }));
    return [
      ...new Map(
        activeDriversList.map((driver) => [driver.value, driver])
      ).values(),
    ];
  }

  @action updateDispatchedDrivers(
    updatedDispatchedDrivers,
    updatedDrivers,
    routeColorList
  ) {
    // if drivers routes are completed by doing end trip, they are no longer updated so we need to remove them from the list
    const updatedDispatchedDriversList = updatedDispatchedDrivers.map(
      (driver) => driver.driverId.toUpperCase()
    );

    const currentDispatchedDrivers = this.dispatchedDrivers.map((d) =>
      d.driverId.toUpperCase()
    );

    const driversToAdd = updatedDispatchedDrivers.filter(
      (driver) =>
        !currentDispatchedDrivers.includes(driver.driverId.toUpperCase())
    );

    const driversToRemove = updatedDrivers
      .map((driverId) => driverId.toUpperCase())
      .filter(
        (driverId) =>
          !updatedDispatchedDriversList.includes(driverId.toUpperCase())
    );

    // remove driverLocations
    driversToRemove.map(
      (driverId) => delete this.driverLocations[driverId.toUpperCase()]
    );

    const dispatchedDrivers = this.dispatchedDrivers
      .filter(
        (driver) => !driversToRemove.includes(driver.driverId.toUpperCase())
      )
      .map((driver) => {
        const updateDriverData = updatedDispatchedDrivers.find(
          (updatedDriver) =>
            updatedDriver.driverId.toUpperCase() ===
            driver.driverId.toUpperCase()
        );
        if (updateDriverData) {
          const {
            lastKnownLatitude,
            lastKnowLongitude,
            lastConnectedTime,
            ...updateDriver
          } = updateDriverData;
          this.addDriverLocation(
            updateDriver.driverId,
            lastKnownLatitude,
            lastKnowLongitude,
            lastConnectedTime
          );
          return {
            ...updateDriver,
            unReadCount: this.getUnreadMessageCounts(updateDriver.driverId),
            dispatchRoutes:
              updateDriver.dispatchRoutes?.map((route) => ({
                ...route,
                color: driver.dispatchRoutes.find(
                  (r) => r.routeId === route.routeId
                )?.color,
              })) ?? [],
          };
        }
        return driver;
      });
    driversToAdd.map((driver, index) => {
      this.addDriverLocation(
        driver.driverId,
        driver.lastKnownLatitude,
        driver.lastKnowLongitude,
        driver.lastConnectedTime
      );
      dispatchedDrivers.push({
        ...driver,
        unReadCount: this.getUnreadMessageCounts(driver.driverId),
        dispatchRoutes:
          driver.dispatchRoutes?.map((route) => ({
            ...route,
            color:
              routeColorList[
                (dispatchedDrivers.length + index) % routeColorList.length
              ],
          })) ?? [],
      });
    });

    this.dispatchedDrivers.replace(dispatchedDrivers);
  }

  @action addUnReadMessageCountToDriver(driverId) {
    const updatedDriversList = this.dispatchedDrivers.map((driver) => {
      if (driver.driverId === driverId) {
        driver["unReadCount"] = driver["unReadCount"] + 1;
        this.updateUnreadMessageCountList(driverId, driver["unReadCount"]);
      }
      return driver;
    });
    this.dispatchedDrivers.replace(updatedDriversList);
  }

  @action clearDriverUnreadCount(driverId) {
    const updatedDriversList = this.dispatchedDrivers.map((driver) => {
      if (driver.driverId === driverId) {
        driver["unReadCount"] = 0;
        this.updateUnreadMessageCountList(driverId, driver["unReadCount"]);
      }
      return driver;
    });
    this.dispatchedDrivers.replace(updatedDriversList);
  }

  @action populateLatestFuelBreakTime(delays) {
    const fuelBreakStops = delays.filter(
      (delay) => delay.type === appConstants.DELAY_STOP_TYPES.FUEL_BREAK
    );
    fuelBreakStops.sort((a, b) => {
      return (
        new Date(`01/01/1970 ${b.startTime}`) -
        new Date(`01/01/1970 ${a.startTime}`)
      );
    });
    return fuelBreakStops[0];
  }
  @action populateLatestLunchBreakTime(delays) {
    const lunchBreakStops = delays.filter(
      (delay) => delay.type ===  appConstants.DELAY_STOP_TYPES.LUNCH_BREAK
    );
    lunchBreakStops.sort((a, b) => {
      return (
        new Date(`01/01/1970 ${b.startTime}`) -
        new Date(`01/01/1970 ${a.startTime}`)
      );
    });
    return lunchBreakStops[0] || null;
  }

  @action getDispatchRoute(routeId) {
    return this.routesList.find((route) => route.routeId === routeId);
  }

  @action getDispatchDriverRoute(driverId, routeId) {
    return this.routesList.find(
      (route) =>
        route.driverId?.toUpperCase() === driverId?.toUpperCase() &&
        route.routeId?.toUpperCase() === routeId?.toUpperCase()
    );
  }

  @action getDriverDetails(driverId) {
    let pendingStops = [];
    let color = null;
    this.routesList.forEach((route) => {
      if (route.driverId === driverId) {
        pendingStops = pendingStops.concat(
          route.stops.filter((stop) => !stop.isStopCompleted)
        );
        color = route.color;
      }
    });
    return {
      pendingStops: pendingStops,
      color: color,
    };
  }

  @action getTractor(tractorNumber) {
    return this.tractors.find(
      (tractor) => tractor.tractorNumber === tractorNumber
    );
  }

  @action getSelectedDriverCurrentRoutes() {
    return this.currentActiveRoutes.filter(
      (route) =>
        this.selectedDispatchDriverIdList.includes(route.driverId) &&
        route.dispatchId
    );
  }

  @action showErrorMessageOnGrid(gridName, value) {
    switch (gridName) {
      case "driversRouteGridMessage":
        this.driversRouteGridMessage = value;
        break;
      case "pickupsGridMessage":
        this.pickupsGridMessage = value;
        break;
      case "driversGridMessage":
        this.driversGridMessage = value;
        break;
      case "dispatchGridMessage":
        this.dispatchGridMessage = value;
        break;
      case "all":
        this.dispatchGridMessage = value;
        this.driversRouteGridMessage = value;
        this.pickupsGridMessage = value;
        this.driversGridMessage = value;
        break;
    }
  }
  @action getPickupData(pickupId) {
    return this.pickupsList.find(
      (pickup) => pickup.pickupId?.toLowerCase() === pickupId?.toLowerCase()
    );
  }

  @action setShowAllDrivers(showAllDrivers) {
    this.showAllDrivers = showAllDrivers;
  }

  @action getDriverRoutes(driverId) {
    return this.routesList.filter((route) => route.driverId === driverId);
  }

  @action updatePickupAcknowledgedStatus(routeId, pickupId) {
    const updatedDispatchedDrivers = this.dispatchedDrivers.map((driver) => {
      if (
        driver.dispatchRoutes.findIndex(
          (route) => route.routeId.toLowerCase() === routeId.toLowerCase()
        ) > -1
      ) {
        return {
          ...driver,
          dispatchRoutes: driver.dispatchRoutes.map((route) => {
            if (route.routeId.toLowerCase() === routeId.toLowerCase()) {
              route.stops.map((stop) => {
                if (stop.pickupId?.toLowerCase() === pickupId.toLowerCase()) {
                  stop.isAcknowledged = true;
                }
                return stop;
              });
            }
            return route;
          }),
        };
      }
      return driver;
    });
    const updatedPickupList = this.pickupsList.map((pickup) => {
      if (pickup.pickupId?.toLowerCase() === pickupId.toLowerCase()) {
        pickup.isAcknowledged = true;
      }
      return pickup;
    });
    this.pickupsList.replace(updatedPickupList);
    this.dispatchedDrivers.replace(updatedDispatchedDrivers);
  }

  @computed get selectedPickupsList() {
    return this.pickupsList
      .filter((pickup) =>
        this.selectedPickupIdList.includes(pickup.pickupId.toLowerCase())
      )
      .map(
        ({
          pickupId,
          shipperLatitude,
          shipperLongitude,
          driverId,
          routeId,
          isCompleted,
          pickupDate,
          isVoided,
          isMissed
        }) => ({
          pickupId,
          position: {
            lat: shipperLatitude,
            lng: shipperLongitude,
          },
          driverId,
          routeId,
          isCompleted,
          isVoided,
          isMissed,
          isFuture: isFuturePickup(pickupDate, this.terminalOffset),
        })
      );
  }

  @computed get dispatchDrivers() {
    return uniqBy(
      this.currentActiveRoutes.map((route) => ({
        label: route.driverName,
        value: route.driverId,
      })),
      "value"
    );
  }

  @computed get routesWithFilteredStops() {
    const selectedRoute =
      this.selectedDriverRouteId &&
      this.routesList.find(
        (route) => route.routeId === this.selectedDriverRouteId
      );
    const routesList = selectedRoute
      ? [selectedRoute]
      : this.currentActiveRoutes;
    return routesList
      .filter((route) => this.selectedRouteNameList.includes(route.routeId))
      .map((route) => {
        const filteredStops = (
          this.stopsFilter.length
            ? route.stops.filter((stop) =>
            isStopFiltered(
              stop,
              this.stopsFilter,
              this.staticClientData?.clientCodes?.serviceLevelList
              )) : []
        ).map((stop) => {
          return {
            ...(stop ?? {}),
            driverName: route.driverName,
            driverNumber: route.driverNumber,
            driverId: route.driverId,
            color: route.color,
          };
        });
        return { ...route, filteredStops };
      });
  }

  @computed get routesList() {
    return this.dispatchedDrivers.reduce((routes, driver) => {
      return [
        ...routes,
        ...(driver.dispatchRoutes?.map((route) => ({
          ...driver,
          dispatchRoutes: undefined,
          ...route,
        })) ?? []),
      ];
    }, []);
  }

  @action getShouldBeVisibleDrivers(availableDriverList=[]){
    const currentUTCDate = getUTCDate(new Date());
    const activeDriverIds = this.dispatchedDrivers.map((driver) =>  driver.driverId);
    return availableDriverList.filter(
      (driver) =>
        !activeDriverIds.includes(driver.driverId) &&
        isSameDay(currentUTCDate, getUTCDate(new Date(driver.scheduledDate))) &&
        isWithinTime(driver.scheduleTime, this.terminalOffset)
    );
  }

  @computed get currentActiveRoutes() {
    const activeRoutes = this.dispatchedDrivers.map((driver) => ({
      ...driver,
      dispatchRoutes: undefined,
      stops: [],
      ...(driver.dispatchRoutes?.find((route) => route.isActiveRoute) ?? []),
    }));
    const scheduledDrivers = this.getShouldBeVisibleDrivers(
      this.availableDriverList.slice()
    ).map((driver) => ({
      currentStatus: driver.driverStatus,
      driverId: driver.driverId,
      driverName: driver.driverName,
      driverNumber: driver.driverNumber,
      driverPhoneNumber: driver.phoneNumber,
      driverStatus: driver.driverStatus,
      loggedInStatus: driver.loggedInStatus,
      driverClockedInTime: driver.clockedInTime,
      driverType: driver.driverType,
      stops: [],
      scheduleTime: driver.scheduleTime,
      isCompanyDriver: driver.isCompanyDriver,
      accruedHours: driver.accruedHours,
      driverClockedInDept: driver.clockedInDept,
      delays: driver.delays||[]
    }));

    return sortBy(activeRoutes.concat(scheduledDrivers), [
      (item) => !Boolean(item.dispatchId), // Sorts to move already dispatched route to top
      (item) =>
        !isDriverReadyForDispatch(
          item.driverClockedInTime,
          item.isCompanyDriver
        ), // Sorts to move drivers not ready to dispatch to bottom
      (item) => item.driverDispatchTime && new Date(item.driverDispatchTime), // sorts by driverDispatch time
      (item) =>
        isDriverReadyForDispatch(item.driverClockedInTime, item.isCompanyDriver)
          ? item.driverClockedInTime && new Date(item.driverClockedInTime)
          : Number(item.driverNumber), // if isReadyForDispatch sort clockedin time else by driverNumber
    ]);
  }

  @action updateSelectedDriverRouteId(routeId) {
    this.selectedDriverRouteId = routeId;
  }
  @action addUnreadMessages(unreadMessages) {
    //add unReadMessages for showing in driver's grid count
    const driverUnreadMessageCount = unreadMessages.map(
      (driverUnreadMessage) => ({
        driverId: driverUnreadMessage.driverId,
        unreadMessageCount: driverUnreadMessage.messages.length,
      }),
    );
    this.driverUnReadMessageCountList.replace(driverUnreadMessageCount);
    this.addUnreadMessagesForNotifications(unreadMessages);
  }

  addUnreadMessagesForNotifications = (unreadMessages) => {
    const allDriversSortedUnReadMessages = unreadMessages
      .reduce((flattenUnReadMessages, unReadMessage) => {
        if (unReadMessage.messages.length === 0) {
          return flattenUnReadMessages;
        }
        return [...flattenUnReadMessages, ...unReadMessage.messages];
      }, [])
      .sort((a, b) => new Date(a.messageDateTs) - new Date(b.messageDateTs));
    this.addDispatchedDriverNotification(allDriversSortedUnReadMessages);
  }

  getUnreadMessageCounts(driverId) {
    const messageCount = this.driverUnReadMessageCountList.find(
      (count) => count.driverId === driverId
    );
    return messageCount?.unreadMessageCount || 0;
  }

  updateUnreadMessageCountList = (driverId, unReadCount) => {
    const driverUnReadMessageCountList = this.driverUnReadMessageCountList.map(
      (messageCount) => {
        if (messageCount.driverId === driverId) {
          messageCount.unreadMessageCount = unReadCount;
        }
        return messageCount;
      }
    );
    this.driverUnReadMessageCountList.replace(driverUnReadMessageCountList);
  };

  @computed get returnCodes() {
    const { clientCodes } = this.staticClientData;
    return clientCodes.returnCodeList.map((data) => ({
      value: data.code,
      label: `(${data.code}) ${data.description}`,
      isException: data.isException,
      displayReturnToTerminal: data.displayReturnToTerminal,
      isArrivalDepartureTimeRequired: data.isArrivalDepartureTimeRequired,
    }));
  }

  @computed get deliveryStatuses() {
    const { clientCodes } = this.staticClientData;
    return clientCodes.deliveryStatusList.map((data) => ({
      value: data.statusCode,
      labelText: data.status,
    }));
  }

  @computed get exceptions() {
    const { clientCodes } = this.staticClientData;
    return clientCodes.exceptionList.map((data) => ({
      value: data.code,
      labelText: data.description,
      isException: data.isException,
    }));
  }

  @computed get limitedAccessList() {
    const { clientCodes } = this.staticClientData;
    return clientCodes.limitedAccessList.map((data) => ({
      value: data.code,
      label: data.description,
    }));
  }

  @computed get packageCodes() {
    const { packageCodes } = this.staticClientData;
    if (packageCodes.length > 0) {
      return packageCodes.map((data) => ({
        value: data.code,
        label: data.description,
      }));
    }
  }

  // using this to show package types along with package code in Create/Edit pickup shipment grid dropdown.
  @computed get packageCodesWithSuffix() {
    const { packageCodes } = this.staticClientData;
    if (packageCodes.length > 0) {
      return packageCodes.map((data) => ({
        value: data.code,
        label: data.description,
        labelSuffix : `(${data.code})`
      }));
    }
  }

  //TODO : Remove isChecked props from here
  @computed get deliveryAccessorials() {
    const accessorialList = this.deliveryAccessorialList;
    return accessorialList.map((data) => ({
      name: data.code,
      labelText: data.description,
      isChecked: false,
    }));
  }

  @computed get pickupAccessorials() {
    const accessorialList = this.pickupAccessorialList;
    return accessorialList.map((data) => ({
      name: data.code,
      labelText: data.description,
    }));
  }

  @action getDeliveryStatusLabelFromCode(deliveryCode) {
    const { clientCodes } = this.staticClientData;
    return clientCodes.deliveryStatusList.find(
      (deliveryStatus) => deliveryStatus.statusCode === deliveryCode
    )?.status;
  }

  @action getExceptionDetails(exceptionCode) {
    const { clientCodes } = this.staticClientData;
    return clientCodes.exceptionList.find(
      (exception) => exception.code === exceptionCode
    );
  }

  @action getReturnDetails(returnCode) {
    return (
      this.staticClientData.clientCodes.returnCodeList.find(
        (returnType) => returnType.code === returnCode
      ) ?? null
    );
  }

  @computed get priorityAllShipmentList() {
    return (
      this.staticClientData?.clientCodes?.serviceLevelList.map(
        (data) => data.serviceLevel
      ) || []
    );
  }

  @computed get priorityShipmentList() {
    return this.staticClientData?.clientCodes?.serviceLevelList.reduce(
      (priorityShipmentList, data) => {
        return data.isVisible
          ? [...priorityShipmentList, data.serviceLevel]
          : [...priorityShipmentList];
      },
      []
    )|| [];
  }

  @computed get priorityShipmentOptions() {
    return (
      this.staticClientData?.clientCodes?.serviceLevelList.reduce(
        (priorityShipmentOptions, data) => {
          return data.isVisible
            ? [
                {
                  value: data.serviceLevel,
                  label: data.serviceLevel,
                },
                ...priorityShipmentOptions,
              ]
            : [...priorityShipmentOptions];
        },
        [{ value: "", label: "None" }]
      ) || []
    );
  }

  @computed get pickupListForOptimization() {
    return this.pickupsList
      .filter(
        (pickup) =>
          !pickup.isCompleted &&
          !pickup.isVoided &&
          !pickup.isMissed &&
          isTodaysPickup(pickup.pickupDate, this.terminalOffset)
      )
      .sort((a, b) =>
        this.selectedPickupIdList.includes(a.pickupId.toLowerCase()) ? -1 : 0
      ); // return all Assigned and Unassigned pickups for optimization
  }


  @action getCurrentDriver(driverId) {
    return this.currentActiveRoutes.find(
      (driver) => driver.driverId.toUpperCase() === driverId.toUpperCase()
    );
  }

  @computed get unreadDriverNotifications() {
    const driverIds = this.dispatchedDrivers.map((driver) =>
      driver.driverId.toUpperCase()
    );
    return this.dispatchedDriverNotifications
      .filter(
        (msg) => driverIds.includes(msg.sender.toUpperCase())
      )
      .map((msg) => {
        const driver = this.getCurrentDriver(msg.sender);
        return { ...msg, driver };
      });
  }

  // we need this in Create Pickup Shipments grid where "None" will be first option in "Service Level" dropdown.
  @computed get serviceLevelShipmentOptions() {
    return (
      this.staticClientData?.clientCodes?.serviceLevelList.reduce(
        (priorityShipmentOptions, data) => {
          return data.isVisible
            ? [
                ...priorityShipmentOptions,
                {
                  value: data.serviceLevel,
                  label: data.serviceLevel,
                },
              ]
            : [...priorityShipmentOptions];
        },
        [{ value: "", label: "None" }]
      ) || []
    );
  }

  @action updateDuplicatePickups(duplicatePickups) {
    this.duplicatePickups.replace(duplicatePickups);
  }
  
  @computed get dispatchedDriversListForOptimization() {
    return this.dispatchedDrivers.map((driver) => ({
      label: `${driver.driverName}`,
      value: driver.driverId,
      labelSuffix: `(${driver.driverNumber})`,
    }));
  }

  @action setMovePickupTerminalList = (movePickupTerminalList) => {
    this.movePickupTerminalList.replace(movePickupTerminalList);
  };

  @computed get dispatchableDrivers(){
    const dispatchedDriverIds = this.dispatchedDrivers.map(
      (driver) => driver.driverId
    );
    return this.availableDriverList.filter(driver => !dispatchedDriverIds.includes(driver.driverId))
  }

  @action getDriverLatLng = (driverId) => {
     const { longitude, latitude } =
       this.driverLocations[driverId.toUpperCase()] ?? {};
     if (longitude && latitude) {
       return { lat: latitude, lng: longitude };
     }
     return null
  }
}

export default new DispatchStore();
